import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import { useBreadcrumbs } from "@/shared/components/layout/DashboardLayout";
import { breadcrumbBuilders } from "@/shared/components/ui/Breadcrumbs";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  LabelWithTooltip,
} from "@/shared/components/ui";
import { TOOLTIPS } from "@/shared/lib/tooltips";
import {
  getStatusVariant,
  getStatusLabel,
  formatCurrency,
  formatDateTime,
  formatPhone,
  formatPercent,
  getCreditTypeLabel,
} from "@/shared/lib/utils";
import {
  ArrowLeft,
  Phone,
  User,
  CreditCard,
  Plus,
  Trash2,
  Pencil,
  Loader2,
  AlertCircle,
  Copy,
  CheckCircle,
  ExternalLink,
} from "lucide-react";
import { NotesSection } from "@/shared/components/NotesSection";
import { TemplateSelector } from "@/shared/components/TemplateSelector";
import { ClientDataSection } from "@/shared/components/ClientDataSection";
import {
  CreditsEditorDialog,
  ScoringEditorDialog,
  CreditworthinessEditorDialog,
  PersonalDataEditorDialog,
} from "@/shared/components/ClientDataEditors";
import { useSessionConsent } from "@/shared/hooks";
import type {
  Session,
  Lead,
  Offer,
  CreateOfferForm,
  CreditType,
  OfferTemplate,
  SharedCredit,
  ScoringResult,
  CreditworthinessResult,
  PersonalDataSnapshot,
  SessionStatus,
} from "@/shared/types";

// Check if session allows offer management (not in terminal state)
const isSessionEditable = (status: SessionStatus | undefined): boolean => {
  if (!status) return false;
  const terminalStatuses: SessionStatus[] = ["completed", "expired", "revoked_by_client"];
  return !terminalStatuses.includes(status);
};

export function SessionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setItems: setBreadcrumbs } = useBreadcrumbs();

  const [isOfferDialogOpen, setIsOfferDialogOpen] = useState(false);
  const [offerToEdit, setOfferToEdit] = useState<Offer | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);

  // Client data editor states
  const [creditsToEdit, setCreditsToEdit] = useState<SharedCredit[] | null>(null);
  const [scoringToEdit, setScoringToEdit] = useState<ScoringResult | null>(null);
  const [creditworthinessToEdit, setCreditworthinessToEdit] = useState<CreditworthinessResult | null>(null);
  const [personalDataToEdit, setPersonalDataToEdit] = useState<PersonalDataSnapshot | null>(null);

  const [offerForm, setOfferForm] = useState<CreateOfferForm>({
    bankName: "",
    productName: "",
    type: "gotowkowy",
    amount: 0,
    period: 12,
    interestRate: 0,
    monthlyPayment: 0,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ["session", id],
    queryFn: () => api.getSession(id!),
    enabled: !!id,
  });

  const session = (data as { session?: Session & { lead?: Lead; offers?: Offer[]; advisorNotes?: string } } | undefined)?.session;
  const sessionToken = session?.token || "";

  // Client data consent management
  const {
    updateSharedData,
    isUpdating: isUpdatingSharedData,
  } = useSessionConsent({ sessionToken, enabled: !!sessionToken });

  const createOfferMutation = useMutation({
    mutationFn: (offerData: CreateOfferForm) => api.createOffer(id!, offerData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session", id] });
      setIsOfferDialogOpen(false);
      setOfferToEdit(null);
      resetOfferForm();
    },
  });

  const updateOfferMutation = useMutation({
    mutationFn: ({ offerId, data }: { offerId: string; data: Partial<CreateOfferForm> }) =>
      api.updateOffer(id!, offerId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session", id] });
      setIsOfferDialogOpen(false);
      setOfferToEdit(null);
      resetOfferForm();
    },
  });

  const deleteOfferMutation = useMutation({
    mutationFn: (offerId: string) => api.deleteOffer(id!, offerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session", id] });
    },
  });

  const completeSessionMutation = useMutation({
    mutationFn: () => api.updateSession(id!, { status: "completed" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session", id] });
      queryClient.invalidateQueries({ queryKey: ["sessions"] });
    },
  });

  // Set breadcrumbs based on session data
  useEffect(() => {
    const clientName = session?.lead?.name;
    setBreadcrumbs(breadcrumbBuilders.sessionDetail(id || "", clientName));
    return () => setBreadcrumbs([]);
  }, [id, session?.lead?.name, setBreadcrumbs]);

  const resetOfferForm = () => {
    setOfferForm({
      bankName: "",
      productName: "",
      type: (session?.lead?.creditType as CreditType) || "gotowkowy",
      amount: session?.lead?.amount || 0,
      period: 12,
      interestRate: 0,
      monthlyPayment: 0,
    });
  };

  const handleCopyToken = async () => {
    if (session?.token) {
      // Use configured client URL or fallback to localhost:5200 for local dev
      const clientBaseUrl = import.meta.env.VITE_CLIENT_URL || 'http://localhost:5200';
      const clientUrl = `${clientBaseUrl}/sesja/${session.token}`;
      await navigator.clipboard.writeText(clientUrl);
      setCopiedToken(true);
      setTimeout(() => setCopiedToken(false), 2000);
    }
  };

  const handleSubmitOffer = (e: React.FormEvent) => {
    e.preventDefault();
    if (offerToEdit) {
      updateOfferMutation.mutate({ offerId: offerToEdit.id, data: offerForm });
    } else {
      createOfferMutation.mutate(offerForm);
    }
  };

  const handleEditOffer = (offer: Offer) => {
    setOfferToEdit(offer);
    setOfferForm({
      bankName: offer.bankName,
      productName: offer.productName,
      type: offer.type,
      amount: offer.amount,
      period: offer.period,
      interestRate: offer.interestRate,
      monthlyPayment: offer.monthlyPayment,
      rrso: offer.rrso,
      commission: offer.commission,
      margin: offer.margin,
      notes: offer.notes,
      internalNotes: offer.internalNotes,
    });
    setIsOfferDialogOpen(true);
  };

  const handleSelectTemplate = (template: OfferTemplate) => {
    setOfferForm((f) => ({
      ...f,
      bankName: template.bankName,
      productName: template.productName,
      type: template.type,
      period: template.defaultPeriod,
      interestRate: template.defaultInterestRate,
      rrso: template.defaultRrso,
      commission: template.defaultCommission,
      margin: template.defaultMargin,
      notes: template.notes,
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
        <p className="text-gray-600">Nie udało się pobrać sesji</p>
        <Button onClick={() => navigate("/sessions")} variant="secondary" className="mt-4">
          Wróć do listy
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/sessions")}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">
              {session.clientData?.name || "Sesja"}
            </h1>
            <Badge variant={getStatusVariant(session.status)}>
              {getStatusLabel(session.status)}
            </Badge>
          </div>
          <p className="text-gray-600 mt-1">
            Utworzono: {formatDateTime(session.createdAt)}
          </p>
        </div>
        {isSessionEditable(session.status) && (
          <Button
            variant="secondary"
            onClick={() => completeSessionMutation.mutate()}
            isLoading={completeSessionMutation.isPending}
          >
            Zakończ sesję
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Client Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Dane klienta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Imię i nazwisko</p>
              <p className="font-medium">{session.clientData?.name || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Telefon</p>
              <p className="font-medium flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {session.clientData?.phone
                  ? formatPhone(session.clientData.phone)
                  : "-"}
              </p>
            </div>
            {session.clientData?.email && (
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium">{session.clientData.email}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lead Info */}
        {session.lead && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Zapytanie
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Typ kredytu</p>
                <p className="font-medium">
                  {getCreditTypeLabel(session.lead.creditType)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Kwota</p>
                <p className="font-medium text-lg">
                  {formatCurrency(session.lead.amount)}
                </p>
              </div>
              {session.lead.message && (
                <div>
                  <p className="text-sm text-gray-600">Wiadomość</p>
                  <p className="text-sm bg-gray-50 p-2 rounded mt-1">
                    {session.lead.message}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Session Token */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5" />
              Link dla klienta
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-3">
              Wyślij ten link klientowi, aby mógł zobaczyć oferty:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-gray-100 px-3 py-2 rounded text-sm truncate">
                /sesja/{session.token?.slice(0, 8)}...
              </code>
              <Button variant="outline" size="sm" onClick={handleCopyToken}>
                {copiedToken ? (
                  <CheckCircle className="w-4 h-4 text-success-600" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Offers */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Oferty ({session.offers?.length || 0})</CardTitle>
          {isSessionEditable(session.status) && (
            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => {
                setOfferToEdit(null);
                resetOfferForm();
                setIsOfferDialogOpen(true);
              }}
            >
              Dodaj ofertę
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {!session.offers || session.offers.length === 0 ? (
            <p className="text-gray-500 text-center py-8">
              Brak ofert. Dodaj pierwszą ofertę dla klienta.
            </p>
          ) : (
            <div className="space-y-4">
              {session.offers.map((offer) => (
                <div
                  key={offer.id}
                  className="border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {offer.bankName}
                      </h4>
                      <p className="text-sm text-gray-600">{offer.productName}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={getStatusVariant(offer.status)}>
                        {getStatusLabel(offer.status)}
                      </Badge>
                      {isSessionEditable(session.status) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditOffer(offer)}
                            title="Edytuj ofertę"
                          >
                            <Pencil className="w-4 h-4 text-gray-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteOfferMutation.mutate(offer.id)}
                            disabled={deleteOfferMutation.isPending}
                            title="Usuń ofertę"
                          >
                            <Trash2 className="w-4 h-4 text-error-600" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Kwota</p>
                      <p className="font-medium">{formatCurrency(offer.amount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Okres</p>
                      <p className="font-medium">{offer.period} mies.</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Oprocentowanie</p>
                      <p className="font-medium">{formatPercent(offer.interestRate)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Rata</p>
                      <p className="font-medium text-primary-600">
                        {formatCurrency(offer.monthlyPayment)}
                      </p>
                    </div>
                    {offer.rrso !== undefined && (
                      <div>
                        <p className="text-gray-600">RRSO</p>
                        <p className="font-medium">{formatPercent(offer.rrso)}</p>
                      </div>
                    )}
                    {offer.commission !== undefined && (
                      <div>
                        <p className="text-gray-600">Prowizja</p>
                        <p className="font-medium">{formatPercent(offer.commission)}</p>
                      </div>
                    )}
                    {offer.margin !== undefined && (
                      <div>
                        <p className="text-gray-600">Marża</p>
                        <p className="font-medium">{formatPercent(offer.margin)}</p>
                      </div>
                    )}
                  </div>

                  {offer.notes && (
                    <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                      {offer.notes}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Client Shared Data */}
      <ClientDataSection
        sessionToken={sessionToken}
        onEditCredits={(credits) => setCreditsToEdit(credits)}
        onEditScoring={(scoring) => setScoringToEdit(scoring)}
        onEditCreditworthiness={(creditworthiness) => setCreditworthinessToEdit(creditworthiness)}
        onEditPersonalData={(personalData) => setPersonalDataToEdit(personalData)}
      />

      {/* Private Notes */}
      <NotesSection
        sessionId={id!}
        initialNotes={session.advisorNotes || ""}
      />

      {/* Add/Edit Offer Dialog */}
      <Dialog open={isOfferDialogOpen} onOpenChange={(open) => {
        setIsOfferDialogOpen(open);
        if (!open) {
          setOfferToEdit(null);
          resetOfferForm();
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{offerToEdit ? "Edytuj ofertę" : "Dodaj ofertę"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitOffer}>
            <DialogBody className="space-y-4">
              {/* Template Selector */}
              <TemplateSelector
                creditType={session?.lead?.creditType}
                onSelect={handleSelectTemplate}
                onManageClick={() => {
                  setIsOfferDialogOpen(false);
                  navigate("/templates");
                }}
              />

              <hr className="border-gray-200" />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Nazwa banku"
                  value={offerForm.bankName}
                  onChange={(e) =>
                    setOfferForm((f) => ({ ...f, bankName: e.target.value }))
                  }
                  required
                />
                <Input
                  label="Nazwa produktu"
                  value={offerForm.productName}
                  onChange={(e) =>
                    setOfferForm((f) => ({ ...f, productName: e.target.value }))
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={
                    <LabelWithTooltip
                      label="Kwota"
                      tooltip={TOOLTIPS.amount}
                      required
                    />
                  }
                  type="number"
                  value={offerForm.amount}
                  onChange={(e) =>
                    setOfferForm((f) => ({ ...f, amount: Number(e.target.value) }))
                  }
                  required
                />
                <Input
                  label={
                    <LabelWithTooltip
                      label="Okres (miesiące)"
                      tooltip={TOOLTIPS.period}
                      required
                    />
                  }
                  type="number"
                  value={offerForm.period}
                  onChange={(e) =>
                    setOfferForm((f) => ({ ...f, period: Number(e.target.value) }))
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={
                    <LabelWithTooltip
                      label="Oprocentowanie (%)"
                      tooltip={TOOLTIPS.interestRate}
                      required
                    />
                  }
                  type="number"
                  step="0.01"
                  value={offerForm.interestRate}
                  onChange={(e) =>
                    setOfferForm((f) => ({
                      ...f,
                      interestRate: Number(e.target.value),
                    }))
                  }
                  required
                />
                <Input
                  label={
                    <LabelWithTooltip
                      label="Rata miesięczna"
                      tooltip={TOOLTIPS.monthlyPayment}
                      required
                    />
                  }
                  type="number"
                  step="0.01"
                  value={offerForm.monthlyPayment}
                  onChange={(e) =>
                    setOfferForm((f) => ({
                      ...f,
                      monthlyPayment: Number(e.target.value),
                    }))
                  }
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={
                    <LabelWithTooltip
                      label="RRSO (%)"
                      tooltip={TOOLTIPS.rrso}
                    />
                  }
                  type="number"
                  step="0.01"
                  value={offerForm.rrso || ""}
                  onChange={(e) =>
                    setOfferForm((f) => ({
                      ...f,
                      rrso: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                />
                <Input
                  label={
                    <LabelWithTooltip
                      label="Prowizja (%)"
                      tooltip={TOOLTIPS.commission}
                    />
                  }
                  type="number"
                  step="0.01"
                  value={offerForm.commission || ""}
                  onChange={(e) =>
                    setOfferForm((f) => ({
                      ...f,
                      commission: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                />
              </div>

              <Input
                label={
                  <LabelWithTooltip
                    label="Uwagi (widoczne dla klienta)"
                    tooltip={TOOLTIPS.clientNotes}
                  />
                }
                value={offerForm.notes || ""}
                onChange={(e) =>
                  setOfferForm((f) => ({ ...f, notes: e.target.value }))
                }
              />

              {(createOfferMutation.error || updateOfferMutation.error) && (
                <div className="p-3 bg-error-50 border border-error-200 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0" />
                  <p className="text-sm text-error-700">
                    {((createOfferMutation.error || updateOfferMutation.error) as Error).message}
                  </p>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsOfferDialogOpen(false)}
              >
                Anuluj
              </Button>
              <Button
                type="submit"
                isLoading={createOfferMutation.isPending || updateOfferMutation.isPending}
              >
                {offerToEdit ? "Zapisz zmiany" : "Dodaj ofertę"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Client Data Editor Dialogs */}
      {creditsToEdit && (
        <CreditsEditorDialog
          isOpen={!!creditsToEdit}
          onClose={() => setCreditsToEdit(null)}
          credits={creditsToEdit}
          onSave={async (credits) => {
            await updateSharedData({ credits });
            setCreditsToEdit(null);
          }}
          isSaving={isUpdatingSharedData}
        />
      )}

      {scoringToEdit && (
        <ScoringEditorDialog
          isOpen={!!scoringToEdit}
          onClose={() => setScoringToEdit(null)}
          scoring={scoringToEdit}
          onSave={async (scoring) => {
            await updateSharedData({ scoring });
            setScoringToEdit(null);
          }}
          isSaving={isUpdatingSharedData}
        />
      )}

      {creditworthinessToEdit && (
        <CreditworthinessEditorDialog
          isOpen={!!creditworthinessToEdit}
          onClose={() => setCreditworthinessToEdit(null)}
          creditworthiness={creditworthinessToEdit}
          onSave={async (creditworthiness) => {
            await updateSharedData({ creditworthiness });
            setCreditworthinessToEdit(null);
          }}
          isSaving={isUpdatingSharedData}
        />
      )}

      {personalDataToEdit && (
        <PersonalDataEditorDialog
          isOpen={!!personalDataToEdit}
          onClose={() => setPersonalDataToEdit(null)}
          personalData={personalDataToEdit}
          onSave={async (personalData) => {
            await updateSharedData({ personalData });
            setPersonalDataToEdit(null);
          }}
          isSaving={isUpdatingSharedData}
        />
      )}
    </div>
  );
}
